// 261005 E2-P2a — thẻ "Nối tập N+1" ghim trên hội thoại (thẻ HIỆN HÀNH từ GET scope). Nút bấm được theo `relinkActionState` (Y5).
import React, { useState } from 'react';
import { Alert, Input, Modal, Popconfirm, Space, Typography } from 'antd';
import Button from '../WbButton';
import type { EpisodeScriptRecord } from '../../../../types/adaptation';
import type { AssistantRelink } from '../../../../types/adaptationAssistant';
import { MIN_RELINK_REASON, isReasonValid, relinkCardView } from '../../episodeFixUtils';
import { relinkActionState, undoAutoState } from './assistantView';
import { BlockBody, BlockShell } from './BlockShell';

const { Text, Paragraph } = Typography;
const ALERT_TYPE: Record<string, 'success' | 'warning' | 'error' | 'info'> = { ok: 'success', warn: 'warning', danger: 'error', neutral: 'info' };
const BOX: React.CSSProperties = { whiteSpace: 'pre-wrap', fontSize: 13, background: 'var(--wb-side)', padding: 6, maxHeight: 200, overflow: 'auto', wordBreak: 'break-word' };

interface Props {
  card: AssistantRelink;
  episode: number;
  editBlocker: string | null;
  nextScript: Pick<EpisodeScriptRecord, 'status' | 'modifiedAt'> | null;
  /** `ctx.busy` hoặc khung đang thao tác. */
  busy: boolean;
  /** E4-P1 — lý do khi `busy` do sửa tay. */
  busyReason?: string | null;
  acting: boolean;
  onRelink: (reason?: string) => Promise<void> | void;
  onRewrite: (from: number) => void;
  /** 261015 P4a (D9) — Hoàn tác một lần HỆ THỐNG tự nối. */
  onUndoAuto?: () => Promise<void> | void;
  /** Đang chạy riêng thao tác Hoàn tác (nút quay vòng). */
  undoing?: boolean;
}

const RelinkCard: React.FC<Props> = ({ card, episode, editBlocker, nextScript, busy, busyReason, acting, onRelink, onRewrite, onUndoAuto, undoing = false }) => {
  const [modal, setModal] = useState(false);
  const [reason, setReason] = useState('');
  const v = relinkCardView(card);
  const gate = relinkActionState({ view: v, nextScript, editBlocker, busy: busy || acting, busyReason: busy ? busyReason : null });
  const undo = undoAutoState({ view: v, nextScript, editBlocker, busy: busy || acting, busyReason: busy ? busyReason : null, relinkedAt: card.relinkedAt });
  const submitReason = async () => { await onRelink(reason.trim()); setModal(false); setReason(''); };

  return (
    <div style={{ margin: '12px 16px 0' }}>
    <BlockShell>
    <BlockBody title={`Nối tập ${v.nextEpisode}`} tone={v.tone} running={v.phase === 'running'}>
      <Alert style={{ margin: '6px 0' }} type={ALERT_TYPE[v.tone]} showIcon message={v.headline} description={v.error} />
      {v.canUndoAuto && onUndoAuto && (
        <div style={{ marginBottom: 4 }}>
          <Popconfirm
            title={`Hoàn tác tự nối tập ${v.nextEpisode}?`} okText="Hoàn tác" cancelText="Thôi" disabled={!undo.canUndo}
            description="Tập sau quay về đoạn nối trước khi tự nối; thẻ trở lại “Cần xem” để bạn nối tay. Bản hiện tại vẫn còn ở Phiên bản."
            onConfirm={() => onUndoAuto()}
          >
            <Button disabled={!undo.canUndo} loading={undoing}>Hoàn tác</Button>
          </Popconfirm>
          {undo.reason && <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 13 }}>{undo.reason}</Text></div>}
        </div>
      )}
      {v.phase === 'verdict' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Đoạn kết MỚI của tập {episode}</Text>
              <div style={BOX}>{v.newExitText || '—'}</div>
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>2 cảnh đầu tập {v.nextEpisode}</Text>
              <div style={BOX}>{v.nextOpeningText || '—'}</div>
            </div>
          </div>
          {v.conflicts.map((c, i) => (
            <Alert key={i} style={{ marginTop: 6 }} type="error" showIcon message={c.problem}
              description={<div style={{ fontSize: 13, wordBreak: 'break-word' }}><div>Đoạn kết mới: “{c.quoteNew}”</div><div>Tập {v.nextEpisode}: “{c.quoteNext}”</div></div>} />
          ))}
          {v.invalidNote && <Text type="secondary" style={{ fontSize: 13 }}>{v.invalidNote}</Text>}
          <Space style={{ marginTop: 8 }} wrap>
            {v.needsReason ? (
              <Button type="primary" disabled={!gate.canRelink} loading={acting} onClick={() => { setReason(''); setModal(true); }}>Nối lại (nhập lý do)</Button>
            ) : (
              <Popconfirm title={`Nối lại tập ${v.nextEpisode} theo đoạn kết mới?`} okText="Nối lại" cancelText="Thôi" onConfirm={() => onRelink()} disabled={!gate.canRelink}>
                <Button type="primary" disabled={!gate.canRelink} loading={acting}>Nối lại</Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={`Viết lại tuần tự từ tập ${v.nextEpisode}?`} description="Tốn AI và ghi đè kịch bản các tập sau — bản cũ vẫn còn ở Phiên bản."
              okText="Viết lại" cancelText="Thôi" onConfirm={() => onRewrite(v.nextEpisode)} disabled={!gate.canRewrite}
            >
              <Button disabled={!gate.canRewrite}>Viết lại tuần tự từ tập {v.nextEpisode}</Button>
            </Popconfirm>
          </Space>
          {gate.reason && <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 13 }}>{gate.reason}</Text></div>}
          <Modal
            open={modal} title={`Nối lại tập ${v.nextEpisode}`} okText="Nối lại" cancelText="Thôi"
            okButtonProps={{ disabled: !isReasonValid(reason), loading: acting }} onCancel={() => setModal(false)} onOk={submitReason}
          >
            <Paragraph>AI {v.tone === 'danger' ? 'báo không nhất quán' : 'chưa kiểm được'} — nếu bạn đã đọc và thấy vẫn nối được, ghi lý do (≥ {MIN_RELINK_REASON} ký tự). Lý do được lưu cùng bản nối.</Paragraph>
            <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="VD: Đã đọc 2 cảnh đầu tập sau, nhân vật vẫn ở đúng chỗ" />
          </Modal>
        </>
      )}
    </BlockBody>
    </BlockShell>
    </div>
  );
};

export default RelinkCard;
